import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { apiErr, API, getApiToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared scraping logic across Google Maps / Facebook / Instagram pages.
export default function useScrape(source) {
  const { refreshQuota } = useAuth();
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [progress, setProgress] = useState(null); // {event, ...data}
  const abortRef = useRef(null);

  // Progress callback for parent components (e.g. ScrapingLoader)
  const onProgress = useCallback((cb) => { progress && cb(progress); }, [progress]);

  const run = async (params) => {
    setLoading(true);
    setResults([]);
    setSelected({});
    setSuggestions(null);
    setProgress({ event: "start" });

    try {
      // Try SSE streaming first
      if (typeof EventSource !== "undefined" || typeof fetch !== "undefined") {
        const ctrl = new AbortController();
        abortRef.current = ctrl;

        const token = await getApiToken();
        const headers = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;

        const res = await fetch(`${API}/scraper/run_stream`, {
          method: "POST",
          headers,
          body: JSON.stringify({ source, ...params }),
          signal: ctrl.signal,
        });

        if (res.ok && res.headers.get("content-type")?.includes("text/event-stream")) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              try {
                const evt = JSON.parse(line.slice(6));
                setProgress(evt);

                if (evt.event === "done") {
                  setResults(evt.results || []);
                  setSuggestions(evt.suggestions || null);
                  const sel = {};
                  (evt.results || []).forEach((r) => {
                    if (!r.already_saved) sel[r.external_id] = true;
                  });
                  setSelected(sel);
                  toast.success(`${evt.count} bisnis ditemukan · ${evt.credits_used} kredit terpakai`);
                  refreshQuota();
                } else if (evt.event === "error") {
                  toast.error(evt.message || "Terjadi kesalahan");
                }
              } catch {}
            }
          }
          setLoading(false);
          setProgress(null);
          return;
        }
        // Fallback: non-streaming response (shouldn't happen, but handle it)
      }

      // Fallback: regular POST
      const { data } = await api.post("/scraper/run", { source, ...params });
      setResults(data.results);
      setSuggestions(data.suggestions || null);
      const sel = {};
      data.results.forEach((r) => { if (!r.already_saved) sel[r.external_id] = true; });
      setSelected(sel);
      toast.success(`${data.count} bisnis ditemukan · ${data.credits_used} kredit terpakai`);
      refreshQuota();
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(apiErr(err));
      }
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    setLoading(false);
    setProgress(null);
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedLeads = results.filter((r) => selected[r.external_id]);

  const save = async () => {
    if (selectedLeads.length === 0) { toast.error("Pilih minimal 1 lead"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads/save", { leads: selectedLeads });
      toast.success(`${data.saved} lead disimpan${data.skipped ? `, ${data.skipped} duplikat dilewati` : ""}`);
      setResults((rs) => rs.map((r) => (selected[r.external_id] ? { ...r, already_saved: true } : r)));
      refreshQuota();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSaving(false);
    }
  };

  return { results, selected, setResults, loading, saving, run, toggle, save, selectedLeads,
           suggestions, progress, onProgress, cancel };
}

// Hook: fetch scrape history
export function useScrapeHistory() {
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async (limit = 20) => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/scraper/history?limit=${limit}`);
      setHistory(data);
    } catch {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  return { history, historyLoading, fetchHistory };
}
